import { zodResolver } from '@hookform/resolvers/zod';
import { ValidationResultResponse } from '@zond/api-interfaces';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@zond/ui';
import { cn, setFormErrors } from '@zond/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../../auth-provider';
import { useLogin } from './auth.queries';
import { GoogleOAuthButton } from './components/GoogleOAuthButton';

export function LoginForm({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const { t } = useTranslation('auth');
  const loginSchema = z.object({
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(8, t('validation.passwordMin')),
  });
  const { setToken } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [loginError, setLoginError] = useState<string | null>(null);
  const { mutate: login, isPending, isSuccess, data } = useLogin();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const navigate = useNavigate();

  if (isSuccess && data) {
    localStorage.setItem('refreshToken', data.refreshToken);
    setTimeout(() => {
      setToken(data.accessToken);
      if (redirectPath) {
        navigate(decodeURIComponent(redirectPath));
      } else {
        navigate('/');
      }
    }, 0);
  }

  const onSubmit = (data: z.infer<typeof loginSchema>) => {
    setLoginError(null);
    login(data, {
      onError: (error) => {
        if (error?.response?.data) {
          setFormErrors(form, error.response.data as ValidationResultResponse);
        } else {
          setLoginError(t('login.errors.failed'));
        }
      },
    });
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('login.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-4">
                <GoogleOAuthButton onError={setLoginError} redirectPath={redirectPath} />
                <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                  <span className="relative z-10 bg-background px-2 text-muted-foreground">
                    {t('login.orContinueWithEmail')}
                  </span>
                </div>
                {loginError && <div className="text-red-500 text-sm">{loginError}</div>}
                <div className="grid gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common:labels.email')}</FormLabel>
                        <FormControl>
                          <Input placeholder="m@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="grid pb-4">
                        <div className="flex items-center">
                          <FormLabel>{t('login.passwordLabel')}</FormLabel>
                          <Button variant="link" asChild className="ml-auto px-0 gap-0 py-0 h-6">
                            <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
                          </Button>
                        </div>
                        <FormControl>
                          <Input type="password" placeholder="********" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? t('login.submitting') : t('login.submit')}
                  </Button>
                </div>
                <div className="text-center text-sm">
                  {t('login.noAccount')}{' '}
                  <Link to="/sign-up" className="underline underline-offset-4">
                    {t('login.signUpLink')}
                  </Link>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        <Trans
          t={t}
          i18nKey="legal.agreement"
          components={{ terms: <Link to="/terms" />, privacy: <Link to="/privacy" /> }}
        />
      </div>
    </div>
  );
}
